import moment from 'moment';
import { Tooltip, Table } from 'antd';
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import { expandIcon, WidgetLoader } from 'datastake-daf/dist/components';
import { challengeCategoryOptions } from './config';
import { useModule } from '../../../../../helpers/user';
import { DashboardService } from '../../../../../services';
import TablePagination from '../../../../Table/pagination';
import { Animated } from '../../../../../screens/Modules/pme/screens/dashboard/widgets/tasks';
import { _empty } from '../../../../../screens/Modules/pme/screens/dashboard/widgets/recTable';
import { renderSeverity } from '../../../../../screens/Analysis/sca-dds-kyc-widgets/table-components/helper';
import { getLocales } from '../../../../Table/newTable';

export default function SupplyChain({ project, language }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const total = data.length;
  const [expandedRows, setExpandedRows] = useState([]);
  const [toggledKeys, setToggledKeys] = useState([]);
  const loadingKeys = useRef({});

  const { mod } = useModule();

  const _fetch = async () => {
    setLoading(true);
    setPage(1);

    try {
      const res = await DashboardService.getPmeProject({
        widget: 'supplyChainMitigation',
        pagination: {
          page: page,
        },
      })

      if (res.data.supplyChainMitigation) {
        const data = res.data.supplyChainMitigation;
        setData(data.map((d, i) => ({
          ...d,
          key: `${i}-${d.id}`,
          challenges: (d.challenges || []).map((ch, j) => ({
            ...ch,
            datastakeId: d.datastakeId,
            name: d.name,
            key: `${i}-${d.id}-${j}`,
            isKid: true,
          })),
        }))
        );
      }
    } catch (err) {
      console.log(err);
      setData([]);
    }

    setLoading(false);
  };

  useEffect(() => {
    _fetch();
  }, [project, language]);

  const columns = useMemo(() => [
    {
      title: t('Supply Chain Participant'),
      dataIndex: 'name',
      className: 'show',
      key: 'name',
      ellipsis: true,
      render: (area, all) => (
        <Tooltip title={area}>
          <a
            href={`/app/${mod}/view/scp/${all.datastakeId}`} target="_blank"
            rel="noreferrer"
            className="ellipsis-text"
          >
            {area}
          </a>
        </Tooltip>
      ),
      width: '25%',
    },
    {
      title: t('Challenges'),
      dataIndex: 'challenges',
      key: 'challenges',
      ellipsis: true,
      className: 'show',
      render: (all, _all) => {
        if (_all.isEmpty) {
          return <div style={{ height: 30 }} />;
        }

        if (_all.challengeText) {
          return <Tooltip title={_all.challengeText}>
            {_all.challengeText}
          </Tooltip>;
        }

        const val = `${(all || []).filter((s) => s.challenge).length} ${t('challenges')}`
        return <Tooltip title={val}>
          {val}
        </Tooltip>;
      },
      width: '25%',
    },
    {
      title: t('Severity'),
      dataIndex: 'challengeSeverity',
      key: 'challengeSeverity',
      ellipsis: true,
      className: 'show',
      width: 130,
      render: (val) => {
        if (val) {
          return renderSeverity({ type: val, t, center: false });
        }

        return null;
      },
    },
    {
      title: t('Last Update'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      ellipsis: true,
      className: 'show',
      width: 130,
      render: (val, all) => {
        if (all.isKid) {
          return val ? moment(val).format('DD MMM YYYY') : '--';
        }
      },
    },
    {
      title: t('Category'),
      dataIndex: 'challengeCategory',
      key: 'challengeCategory',
      ellipsis: true,
      className: 'show',
      render: (val) => {
        if (val) {
          const _opt = challengeCategoryOptions[val];
          if (_opt) {
            return _opt[language] || _opt.en;
          }
        }

      },
      width: '25%',
    },
    {
      title: t('Recommendations'),
      dataIndex: 'recomendations',
      key: 'recomendations',
      ellipsis: true,
      className: 'show',
      width: '25%',
      render: (v, all) => {
        if (all.isEmpty) {
          return null;
        }

        if (all.title) {
          return <Tooltip title={all.title}>
            {all.title}
          </Tooltip>;
        }
        const val = `${(all.challenges || []).length} ${t('recommendations')}`
        return <Tooltip title={val}>
          {val}
        </Tooltip>;
      },
    },
  ], [t, language]);

  const pages = useMemo(() => Math.ceil(total / 8), [total]);

  const canGoNext = useMemo(() => {
    if (total === 0) {
      return false;
    }

    if (page !== pages) {
      return true;
    }

    return false;
  }, [page, total, pages]);

  const canGoPrev = useMemo(() => page !== 1, [page]);

  const goNext = () => {
    const newPage = page + 1;
    setPage(newPage);
  };

  const goPrev = () => {
    const newPage = page - 1;
    setPage(newPage);
  };

  return (
    <div className='overflow-section-3 daf-table-wrapper'>
      {loading && (
        <WidgetLoader background="white" />
      )}
      <Table
        locale={getLocales(t)}
        size="small"
        columns={columns}
        sticky
        scroll={{ y: 425 }}
        style={{ minHeight: 425 }}
        pagination={{
          current: page,
          total: total,
          pageSize: 8,
          onChange: (e) => { },
          showSizeChanger: false,
        }}
        expandable={{
          onExpandedRowsChange: (r) => {
            setExpandedRows(r);
          },
          expandedRowKeys: expandedRows,
          rowExpandable: (r) => (r?.challenges || []).length,
          expandedRowRender: (r) => {
            return (
              <Animated isActive={toggledKeys.includes(r.key)}>
                <Table
                  locale={getLocales(t)}
                  className="no-header-table"
                  columns={columns}
                  dataSource={r.challenges}
                  expandable
                  expandedRowRender={() => null}
                  doInject={false}
                  expandIcon={() => <button type="button" className="ant-table-row-expand-icon ant-table-row-expand-icon-spaced" aria-label="Expand row"></button>}
                  pagination={false}
                />
              </Animated>
            )
          }
        }}
        rowClassName={(rec) => expandedRows.includes(rec.key) ? 'expanded' : ''}
        expandIcon={(props) => expandIcon({ ...props, arrows: true, toggledKeys, setToggledKeys, loadingKeys })}
        dataSource={data.length ? data : _empty}
      />
      <TablePagination
        t={t}
        page={page}
        totalPages={pages}
        goPrev={goPrev}
        goNext={goNext}
        canGoNext={canGoNext}
        canGoPrev={canGoPrev}
        totalItems={total}
        doTotalItems
        perPage={8}
      />
    </div>
  );
}
